import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChoiceGroup, Field } from "@/components/site/Form";
import { useJourney, useSite } from "@/lib/site";

export const StepNav = ({ back, onNext, nextLabel = "Continue", busy, testid = "step-next-button", cta = false }) => (
  <div className="mt-8 flex flex-col-reverse gap-3 border-t border-border pt-6 sm:flex-row sm:justify-between">
    {back ? <Button type="button" variant="ghost" onClick={back} data-testid="step-back-button">Back</Button> : <span />}
    <Button type="button" size="lg" className={`h-12 text-base ${cta ? "btn-cta" : ""}`} onClick={onNext} disabled={busy} data-testid={testid}>{nextLabel}</Button>
  </div>
);

export const IntentStep = ({ next }) => {
  const j = useJourney();
  const { cfg } = useSite();
  const [err, setErr] = useState(null);
  const s = j.state;
  const px = s.partExchange || {};
  const setPx = (patch) => j.update({ partExchange: { ...px, ...patch } });
  const onNext = () => {
    if (!s.intent) return setErr("Please choose one option.");
    if (px.listing_url && !/^https?:\/\/\S+$/.test(px.listing_url.trim())) return setErr("Please paste the full link, starting with https://");
    next();
  };
  return (
    <section data-testid="intent-step">
      <h1 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">What would you like to do?</h1>
      <div className="mt-6 grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="What would you like to do?">
        {[["sell", "Sell my car outright", "Sell your car to Harrington's Direct."], ["part_exchange", "Enquire about part exchange", "Put your car towards one of ours."]].filter(([v]) => !(v === "sell" && s.valuation?.status === "not_buying")).map(([v, t, d]) => (
          <label key={v} className={`choice flex-col items-start gap-1 p-4 ${s.intent === v ? "choice-on" : ""}`} data-testid={`intent-${v}`}>
            <input type="radio" className="sr-only" name="intent" checked={s.intent === v} onChange={() => { j.update({ intent: v }); setErr(null); }} />
            <span className="text-base font-bold text-slate-900">{t}</span>
            <span className="text-sm font-normal text-slate-600">{d}</span>
          </label>
        ))}
      </div>
      {s.intent === "part_exchange" && (
        <div className="fade-in mt-6 space-y-5 rounded-md border border-border bg-white p-4">
          <p className="text-sm text-slate-700" data-testid="px-note">Any part-exchange value is agreed separately and depends on the car you choose – an online estimate is not a part-exchange allowance.</p>
          <p className="text-sm text-slate-700">
            Seen a car you like? <a className="link inline-flex items-center gap-1" href={cfg.business.stock_url} target="_blank" rel="noopener noreferrer" data-testid="px-browse-stock-link">Browse our cars <ExternalLink className="h-3.5 w-3.5" /></a> and paste its link below, or describe what you're looking for.
          </p>
          <Field label="Link to the car you're interested in" htmlFor="px-url" optional>
            <input id="px-url" type="url" inputMode="url" className="field-input" placeholder="https://hcsales.co.uk/vehicle/…" value={px.listing_url || ""}
                   onChange={(e) => setPx({ listing_url: e.target.value })} data-testid="px-listing-url-input" />
          </Field>
          <Field label="Or describe the car you'd like" htmlFor="px-desc" optional>
            <textarea id="px-desc" rows={3} maxLength={1000} className="field-input" placeholder="e.g. a small automatic hatchback, under 40,000 miles"
                      value={px.description || ""} onChange={(e) => setPx({ description: e.target.value })} data-testid="px-description-input" />
          </Field>
        </div>
      )}
      {err && <p className="mt-4 text-sm font-medium text-destructive" role="alert" data-testid="intent-error">{err}</p>}
      <StepNav onNext={onNext} />
    </section>
  );
};

export { ChoiceGroup };
